import Radar from "radar-sdk-js";
import { bootPhase } from "./boot-perf";

/**
 * IP geolocation, started as early as possible.
 *
 * This used to run inside the map's `onLoad`, which meant the map painted at a
 * default location and then visibly jumped once the result arrived - discarding
 * a full viewport of tiles in the process. Starting it during shell render lets
 * the request overlap the ~311KB map chunk download, so the map can initialise
 * at the right place the first time.
 *
 * The request is wrapped in `bootPhase` so its duration lands in the boot
 * report next to bundle-eval / react-mount / remote-config. The default timeout
 * below is a starting value to be replaced with a measured one - read the real
 * distribution from production with `?perf=1`.
 */

export type IpGeolocationOutcome = "pending" | "ok" | "timeout" | "failed";

const DEFAULT_TIMEOUT_MS = 800;

let inFlight: Promise<[number, number] | null> | undefined;
let outcome: IpGeolocationOutcome = "pending";
// Wall-clock time the request actually started, so a caller who shows up
// late (after redux-persist rehydration, the remote-config gate, and the
// ~311KB map chunk download) spends only what is left of the budget instead
// of a fresh timeout stacked on top of all of that.
let startedAt = 0;

/** Begin the request. Idempotent - later calls reuse the first one. */
export const startIpGeolocation = (): void => {
  if (inFlight) return;

  startedAt = Date.now();
  inFlight = bootPhase("ip-geolocation", async () => {
    try {
      const result = await Radar.ipGeocode();
      const latitude = result.address?.latitude;
      const longitude = result.address?.longitude;
      if (latitude === undefined || longitude === undefined) {
        outcome = "failed";
        return null;
      }
      outcome = "ok";
      return [longitude, latitude] as [number, number];
    } catch {
      // Ad blockers and offline clients land here. Not an error worth
      // surfacing - the caller has a fallback.
      outcome = "failed";
      return null;
    }
  }).catch(() => {
    // bootPhase itself can reject: performance.mark() is unguarded in
    // boot-perf.ts, and an async function turns that throw into a rejection.
    // Fail open - a telemetry failure must never deny the caller an answer.
    // Guard like the timeout branch does: bootMarkEnd's mark() runs in a
    // `finally` after the geocode logic above has already set a truthful
    // outcome, so only fall back to "failed" if nothing else settled it.
    if (outcome === "pending") outcome = "failed";
    return null;
  });
};

/**
 * Resolve the initial map center as [lng, lat], or null if the lookup failed
 * or outran `timeoutMs`. Never rejects: the caller must always get an answer
 * inside the budget so the map is never held back.
 */
export const getInitialCenter = (
  timeoutMs: number = DEFAULT_TIMEOUT_MS
): Promise<[number, number] | null> => {
  startIpGeolocation();

  // Spend only what is left of the budget, measured from when the request
  // actually started (startedAt), not from now. A caller that shows up after
  // startIpGeolocation() already fired earlier - e.g. CampgroundMap, well
  // after App.tsx's module-scope kickoff - must not stack a fresh full
  // timeout on top of time that has already elapsed.
  const remaining = Math.max(0, timeoutMs - (Date.now() - startedAt));

  const timeout = new Promise<null>((resolve) => {
    setTimeout(() => {
      // Only claim a timeout if nothing else has already settled it - a request
      // that failed fast must keep reporting "failed", since the two call for
      // different fixes.
      if (outcome === "pending") outcome = "timeout";
      resolve(null);
    }, remaining);
  });

  return Promise.race([inFlight as Promise<[number, number] | null>, timeout]);
};

/** Which way the lookup went. Distinguishes a slow lookup from a broken one. */
export const getIpGeolocationOutcome = (): IpGeolocationOutcome => outcome;

/** Test-only. Clears module state between cases. */
export const resetIpGeolocationForTests = (): void => {
  inFlight = undefined;
  outcome = "pending";
  startedAt = 0;
};
